import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faStar } from '@fortawesome/free-solid-svg-icons';
import '../../styles/LandingPage.css';
import '../../styles/ReportsDashboard.css';
import { getReportIcon } from '../../utils/reportIcons';

const PINNED_KEY = 'reports-dashboard-pins';
const COLLAPSED_KEY = 'reports-dashboard-collapsed';

const ReportsDashboard = () => {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pinnedIds, setPinnedIds] = useState([]);
    const [collapsed, setCollapsed] = useState({});

    useEffect(() => {
        fetchReports();
        try {
            const pins = JSON.parse(localStorage.getItem(PINNED_KEY) || '[]');
            setPinnedIds(Array.isArray(pins) ? pins.map(Number) : []);
        } catch (err) {
            setPinnedIds([]);
        }
        try {
            const stored = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '{}');
            setCollapsed(stored && typeof stored === 'object' ? stored : {});
        } catch (err) {
            setCollapsed({});
        }
    }, []);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/reports`);
            setReports(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const persistPins = (next) => {
        setPinnedIds(next);
        localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    };

    const togglePin = (event, reportId) => {
        event.preventDefault();
        event.stopPropagation();
        const id = Number(reportId);
        persistPins(pinnedIds.includes(id) ? pinnedIds.filter((item) => item !== id) : [...pinnedIds, id]);
    };

    const toggleSection = (category) => {
        const next = { ...collapsed, [category]: !collapsed[category] };
        setCollapsed(next);
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return reports;
        return reports.filter((report) => {
            const title = String(report.report_title || '').toLowerCase();
            const description = String(report.description || '').toLowerCase();
            const category = String(report.category || '').toLowerCase();
            return title.includes(term) || description.includes(term) || category.includes(term);
        });
    }, [reports, search]);

    const sections = useMemo(() => {
        const pinned = filtered.filter((report) => pinnedIds.includes(Number(report.report_id)));
        const byCategory = {};
        filtered.forEach((report) => {
            const category = report.category || 'Uncategorized';
            if (!byCategory[category]) byCategory[category] = [];
            byCategory[category].push(report);
        });
        Object.values(byCategory).forEach((list) => {
            list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.report_title).localeCompare(String(b.report_title)));
        });
        const categories = Object.keys(byCategory).sort((a, b) => {
            if (a === 'Uncategorized') return 1;
            if (b === 'Uncategorized') return -1;
            return a.localeCompare(b);
        });
        return { pinned, categories, byCategory };
    }, [filtered, pinnedIds]);

    const renderCard = (report) => {
        const id = Number(report.report_id);
        const pinned = pinnedIds.includes(id);
        return (
            <Link
                key={id}
                to={`/reports-dashboard/${id}`}
                className="link-card report-card"
            >
                <button
                    type="button"
                    className="report-card-pin"
                    title={pinned ? 'Unpin report' : 'Pin report'}
                    onClick={(event) => togglePin(event, id)}
                >
                    <FontAwesomeIcon icon={faStar} style={{ opacity: pinned ? 1 : 0.35 }} />
                </button>
                <FontAwesomeIcon icon={getReportIcon(report.icon)} size="3x" className="icon" />
                <p>{report.report_title}</p>
                {report.description ? (
                    <span className="report-card-description">{report.description}</span>
                ) : null}
            </Link>
        );
    };

    if (isLoading) {
        return (
            <div className="landing-page">
                <div className="loading-container">
                    <p>Loading reports...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="landing-page">
            <div className="reports-dashboard">
                <div className="reports-dashboard-toolbar">
                    <input
                        type="search"
                        className="reports-dashboard-search"
                        placeholder="Search reports"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        aria-label="Search reports"
                    />
                </div>

                {filtered.length === 0 ? (
                    <div className="reports-dashboard-empty">
                        {reports.length === 0
                            ? 'No reports are available for your role.'
                            : 'No reports match your search.'}
                    </div>
                ) : (
                    <>
                        {sections.pinned.length > 0 && (
                            <section className="reports-section">
                                <button
                                    type="button"
                                    className="reports-section-header"
                                    onClick={() => toggleSection('Pinned')}
                                >
                                    <FontAwesomeIcon icon={collapsed.Pinned ? faChevronRight : faChevronDown} />
                                    Pinned
                                    <span className="reports-section-count">({sections.pinned.length})</span>
                                </button>
                                {!collapsed.Pinned && (
                                    <div className="links-container">
                                        {sections.pinned.map(renderCard)}
                                    </div>
                                )}
                            </section>
                        )}
                        {sections.categories.map((category) => (
                            <section key={category} className="reports-section">
                                <button
                                    type="button"
                                    className="reports-section-header"
                                    onClick={() => toggleSection(category)}
                                >
                                    <FontAwesomeIcon icon={collapsed[category] ? faChevronRight : faChevronDown} />
                                    {category}
                                    <span className="reports-section-count">({sections.byCategory[category].length})</span>
                                </button>
                                {!collapsed[category] && (
                                    <div className="links-container">
                                        {sections.byCategory[category].map(renderCard)}
                                    </div>
                                )}
                            </section>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default ReportsDashboard;
